import type { Dispatch } from 'react';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import type { IUserInformationFromApi } from '../../../slices/user';
import { setUserInformation } from '../../../slices/user';
import type { JSONSerializableObject } from '../../../utils/json-types';
import { setEmployeeId } from '../../../slices/auth';

export const useUpdateUserInformation = (
  updateDataAdditionally?: (
    dispatch: Dispatch<unknown>,
    newData: IUserInformationFromApi,
  ) => void,
) => {
  const dispatch = useDispatch();

  const updateData = useCallback(
    (newData: IUserInformationFromApi) => {
      dispatch(setUserInformation(newData.information));
      if (newData.employee_id) {
        dispatch(setEmployeeId(newData.employee_id));
      }
      if (updateDataAdditionally) {
        updateDataAdditionally(dispatch, newData);
      }
    },
    [dispatch, updateDataAdditionally],
  );

  const processResponse = useCallback(
    (
      responseData: JSONSerializableObject,
      shouldUpdateDataAfterRequest?: boolean,
    ): IUserInformationFromApi | undefined => {
      // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
      const result = (responseData || undefined) as unknown as
        | IUserInformationFromApi
        | undefined;
      if (shouldUpdateDataAfterRequest) {
        if (result) {
          updateData(result);
        } else {
          console.error(
            'shouldUpdateDataAfterRequest was true but responseData was not present',
          );
        }
      }
      return result;
    },
    [updateData],
  );

  return useMemo(
    () => ({
      updateData,
      processResponse,
    }),
    [updateData, processResponse],
  );
};
